/**
 * Handler: search event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontProduct } from '../../types/storefront';
import { adaptSearch } from '../../adapters/search';
import { trackKlaviyoSearchDirect } from '../fallbacks/klaviyo';
import { sendGA4Fallback, mapGA4Item } from '../fallbacks/ga4';
import { gtmMarketFields } from '../utils';

export function handleSearch(
  analyzify: WindowAnalyzify,
  term: string,
  results: StorefrontProduct[],
  resultsCount: number
): void {
  const searchObj = adaptSearch(term, results, resultsCount);

  // GA4 - CDN's gaSearch is search-page gated, fall back to direct gtag
  if (typeof analyzify.gaSearch === 'function') {
    analyzify.gaSearch(searchObj);
  } else {
    // CDN fires 'search' UNCONDITIONALLY to all GA4 groups (no per-event toggle),
    // then 'view_item_list' with the search products.
    const winGa = window as any;
    const ga4PropsSearch = analyzify.properties?.GA4;
    if (typeof winGa.gtag === 'function' && ga4PropsSearch?.status) {
      if (ga4PropsSearch.primary?.status && ga4PropsSearch.primary.id) {
        winGa.gtag('event', 'search', { send_to: ga4PropsSearch.primary.id, search_term: term });
      }
      if (ga4PropsSearch.secondary?.status && ga4PropsSearch.secondary.id) {
        winGa.gtag('event', 'search', { send_to: ga4PropsSearch.secondary.id, search_term: term });
      }
    }

    const searchProducts = searchObj.products || [];
    if (searchProducts.length > 0) {
      const searchListName = `Search Results: ${term}`;
      const ga4Items = searchProducts.map((p: any, i: number) => {
        const firstVariant = p.variants?.[0] || {};
        return mapGA4Item(analyzify, p, firstVariant, i, 'search_results', searchListName);
      });
      // CDN: formatPrice(products.reduce((acc, curr) => acc + formatPrice(curr?.price), 0), true)
      const rawValue = searchProducts.reduce(
        (sum: number, p: any) => sum + (analyzify.formatPrice?.(p.price) || 0),
        0
      );

      sendGA4Fallback(analyzify, 'view_item_list', {
        currency: analyzify.currency || 'USD',
        item_list_id: 'search_results',
        item_list_name: searchListName,
        value: analyzify.formatPrice?.(rawValue, true) ?? rawValue,
        total_quantity: resultsCount || 1,
        total_items: resultsCount || 1,
        items: ga4Items,
      });
    }
  }

  // Facebook
  if (typeof analyzify.fbSearch === 'function') {
    analyzify.fbSearch(searchObj);
  }

  // TikTok
  if (typeof analyzify.ttSearch === 'function') {
    analyzify.ttSearch(searchObj);
  }

  // GTM - CDN doesn't have gtmSearch, push to dataLayer directly
  if (typeof analyzify.gtmSearch === 'function') {
    analyzify.gtmSearch(searchObj);
  } else if (Array.isArray((window as any).dataLayer)) {
    const props = analyzify.properties;
    if (props?.GTM?.status) {
      const searchProducts = searchObj.products || [];
      const items = searchProducts.map((item: any, index: number) => {
        const firstVariant = item.variants?.[0];
        return {
          id: analyzify.getShopifyId?.(
            analyzify.feed_region || '',
            item.id,
            firstVariant?.id,
            firstVariant?.sku
          ) || null,
          item_id: item.id?.toString() || null,
          item_name: item.product_title?.trim() || item.title?.trim() || null,
          item_list_name: `Search Results: ${term}`,
          item_list_id: null,
          item_barcode: null,
          position: index,
          price: analyzify.formatPrice?.(item.price, true) ?? (item.price ? item.price / 100 : 0),
          item_category: item.product_type || item.type || null,
          item_brand: item.vendor || null,
          item_variant: firstVariant?.title?.trim() || null,
          item_variant_id: firstVariant?.id?.toString() || null,
          item_sku: firstVariant?.sku || null,
          quantity: item.quantity || 1,
        };
      });
      const totalValue = items.reduce((acc: number, item: any) => acc + (item.price || 0) * (item.quantity || 1), 0);

      (window as any).dataLayer.push({ ecommerce: null });
      (window as any).dataLayer.push({
        event: 'ee_search',
        ss_event_id: analyzify.eventId.get('search') || null,
        page_type: 'search',
        cart_id: analyzify.cart_id || null,
        search_term: term,
        value: analyzify.formatPrice?.(totalValue, false) ?? totalValue,
        currency: analyzify.currency || 'USD',
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        ecommerce: {
          item_list_name: `Search Results: ${term}`,
          item_list_id: null,
          items,
        },
        ...gtmMarketFields(analyzify),
      });
    }
  }

  // Bing
  if (typeof analyzify.bingSearch === 'function') {
    analyzify.bingSearch(searchObj);
  }

  // Klaviyo - try CDN first, fall back to direct API
  const klaviyoPropsSearch = analyzify.properties?.KLAVIYO;
  if (klaviyoPropsSearch?.status && klaviyoPropsSearch.events?.search) {
    if (typeof analyzify.klaviyoSearch === 'function') {
      try {
        analyzify.klaviyoSearch(searchObj);
      } catch (error) {
        trackKlaviyoSearchDirect(analyzify, term, resultsCount);
      }
    } else {
      trackKlaviyoSearchDirect(analyzify, term, resultsCount);
    }
  }

  // X
  if (typeof analyzify.xSearchResults === 'function') {
    analyzify.xSearchResults(searchObj);
  }

  // Server-side
  if (typeof analyzify.ssSearch === 'function') {
    analyzify.ssSearch(searchObj);
  }
}
